/*!
 * Configuration Manager
 * Main component for managing configurations across environments
 */
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  Alert,
  Spinner,
  Input,
} from "reactstrap";
import ConfigViewer from "./components/ConfigViewer";
import configManagerApi from "services/configManagerApi";

function ConfigManager() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin/');
  
  const userId = 'anonymous'; // TODO: Get from auth context
  
  const [selectedLayer, setSelectedLayer] = useState('backend'); // backend, frontend, middleware, storage
  const [configs, setConfigs] = useState({
    dev: null,
    test: null,
    prod: null,
  });
  const [permissions, setPermissions] = useState({
    dev: { view: true, edit: true },
    test: { view: true, edit: true },
    prod: { view: true, edit: true },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Session state: track active editing sessions per environment
  const [sessions, setSessions] = useState({
    dev: null,
    test: null,
    prod: null,
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({
    dev: false,
    test: false,
    prod: false,
  });

  useEffect(() => {
    loadConfigurations();
    loadPermissions();
  }, [selectedLayer]); // Reload when layer changes

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await configManagerApi.getAllConfigs(selectedLayer);
      
      if (response.status === 'success') {
        setConfigs({
          dev: response.configs.dev || {},
          test: response.configs.test || {},
          prod: response.configs.prod || {},
        });
      } else {
        throw new Error(response.message || 'Failed to load configurations');
      }
    } catch (err) {
      console.error('Error loading configurations:', err);
      setError(err.message || 'Failed to load configurations. Make sure the backend API is running on port 5001.');
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await configManagerApi.getPermissions();
      if (response.status === 'success') {
        setPermissions(response.permissions);
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      // Default permissions already set in state
    }
  };

  const handleConfigUpdate = async (environment, updatedConfig) => {
    try {
      setError(null);
      const response = await configManagerApi.updateConfig(environment, updatedConfig, selectedLayer);
      
      if (response.status === 'success') {
        // Reload configurations
        await loadConfigurations();
        return { success: true, message: `Configuration updated for ${environment}` };
      } else {
        throw new Error(response.message || 'Failed to update configuration');
      }
    } catch (err) {
      console.error('Error updating configuration:', err);
      return { success: false, message: err.message || 'Failed to update configuration' };
    }
  };

  const handleStartSession = async (environment) => {
    try {
      const response = await configManagerApi.startSession(environment, selectedLayer, userId);
      if (response.status === 'success') {
        setSessions(prev => ({
          ...prev,
          [environment]: response.session
        }));
        return { success: true, session: response.session };
      }
    } catch (err) {
      console.error(`Error starting session for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  const handleSaveDiff = async (environment, field, oldValue, newValue) => {
    const session = sessions[environment];
    if (!session) return { success: false, message: 'No active session' };
    
    try {
      const response = await configManagerApi.saveDiff(environment, session.sessionId, field, oldValue, newValue, selectedLayer);
      if (response.status === 'success') {
        setHasUnsavedChanges(prev => ({
          ...prev,
          [environment]: true
        }));
        return { success: true };
      }
    } catch (err) {
      console.error(`Error saving diff for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  const handleSaveSession = async (environment) => {
    const session = sessions[environment];
    if (!session) return { success: false, message: 'No active session' };
    
    try {
      const response = await configManagerApi.saveSession(environment, session.sessionId);
      if (response.status === 'success') {
        setSessions(prev => ({
          ...prev,
          [environment]: response.session
        }));
        return { success: true };
      }
    } catch (err) {
      console.error(`Error saving session for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  const handleCommitSession = async (environment) => {
    const session = sessions[environment];
    if (!session) return { success: false, message: 'No active session' };
    
    try {
      const response = await configManagerApi.commitSession(environment, session.sessionId, selectedLayer);
      if (response.status === 'success') {
        setSessions(prev => ({
          ...prev,
          [environment]: null
        }));
        setHasUnsavedChanges(prev => ({
          ...prev,
          [environment]: false
        }));
        await loadConfigurations();
        return { success: true };
      }
    } catch (err) {
      console.error(`Error committing session for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  const handleSaveAndCommit = async (environment) => {
    const session = sessions[environment];
    if (!session) return { success: false, message: 'No active session' };
    
    try {
      const response = await configManagerApi.saveAndCommit(environment, session.sessionId, selectedLayer);
      if (response.status === 'success') {
        setSessions(prev => ({
          ...prev,
          [environment]: null
        }));
        setHasUnsavedChanges(prev => ({
          ...prev,
          [environment]: false
        }));
        await loadConfigurations();
        return { success: true };
      }
    } catch (err) {
      console.error(`Error save-committing session for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  const handleCancelSession = async (environment) => {
    const session = sessions[environment];
    if (!session) return { success: false, message: 'No active session' };
    
    try {
      const response = await configManagerApi.cancelSession(environment, session.sessionId);
      if (response.status === 'success') {
        setSessions(prev => ({
          ...prev,
          [environment]: null
        }));
        setHasUnsavedChanges(prev => ({
          ...prev,
          [environment]: false
        }));
        await loadConfigurations();
        return { success: true };
      }
    } catch (err) {
      console.error(`Error cancelling session for ${environment}:`, err);
      return { success: false, message: err.message };
    }
  };

  if (loading) {
    return (
      <div className="content" style={isAdminRoute ? { paddingLeft: '30px' } : {}}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spinner color="primary" />
          <p style={{ marginTop: '10px', color: '#e2e8f0' }}>Loading configurations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content" style={isAdminRoute ? { paddingLeft: '30px' } : {}}>
      <Row>
        <Col>
          <Card style={{ backgroundColor: '#2d3748', border: 'none' }}>
            <CardHeader style={{ backgroundColor: '#1a202c', borderBottom: '1px solid #4a5568', padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: 1 }}>
                  <h4 style={{ color: '#f7fafc', margin: 0, fontSize: '1.5rem', display: 'inline-block', marginRight: '1rem' }}>
                    Configuration Manager
                  </h4>
                  <Input
                    type="select"
                    value={selectedLayer}
                    onChange={(e) => setSelectedLayer(e.target.value)}
                    style={{
                      display: 'inline-block',
                      width: 'auto',
                      minWidth: '200px',
                      backgroundColor: '#2d3748',
                      color: '#f7fafc',
                      border: '1px solid #4a5568',
                      fontSize: '0.875rem',
                      padding: '0.375rem 0.75rem',
                    }}
                  >
                    <option value="backend">Backend</option>
                    <option value="storage">Storage</option>
                    <option value="frontend">Frontend</option>
                    <option value="middleware">Middleware</option>
                  </Input>
                </div>
                <p style={{ color: '#a0aec0', margin: '0.5rem 0 0 0', fontSize: '0.875rem', width: '100%' }}>
                  Manage configurations across Development, Test, and Production environments
                </p>
              </div>
            </CardHeader>
            <CardBody style={{ padding: 0 }}>
              {error && (
                <Alert color="danger" style={{ margin: '1rem', backgroundColor: '#742a2a', borderColor: '#c53030', color: '#f7fafc' }}>
                  {error}
                  <br />
                  <small>Make sure the backend API is running: <code>cd backend/config_manager && python3 config_manager_api.py</code></small>
                </Alert>
              )}
              
              <ConfigViewer
                configs={configs}
                permissions={permissions}
                onConfigUpdate={handleConfigUpdate}
                onReload={loadConfigurations}
                selectedLayer={selectedLayer}
                sessions={sessions}
                hasUnsavedChanges={hasUnsavedChanges}
                onStartSession={handleStartSession}
                onSaveDiff={handleSaveDiff}
                onSaveSession={handleSaveSession}
                onCommitSession={handleCommitSession}
                onSaveAndCommit={handleSaveAndCommit}
                onCancelSession={handleCancelSession}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default ConfigManager;
